import {
  Button,
  Cell,
  ProgressBar,
  SearchBarProps,
} from '@zeiss/beyond-online-react';
import { Box, EmptyState, Grid } from '@zeiss/lib-components';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useOutletContext, useParams } from 'react-router-dom';

import { TenantCard } from './';
import { OutletContextType } from './types';
import { ActionBar } from 'src/components/shared/ActionBar';
import { useTenants } from 'src/hooks/api';
import { StandardRouteParams } from 'src/types';

export const Tenants = () => {
  const { t } = useTranslation(['common']);

  const { applicationListId } = useParams<StandardRouteParams>();
  const { canCreateTenant } = useOutletContext<OutletContextType>();
  const navigate = useNavigate();

  const [searchQuery, setSearchQuery] = React.useState<string>('');

  const {
    data: tenants,
    error: errorTenants,
    isPending: isPendingTenants,
  } = useTenants({ applicationListId, searchQuery });

  const handleSearch: SearchBarProps['onSearch'] = (_, { searchValue }) => {
    setSearchQuery(searchValue);
  };

  const renderResult = React.useCallback(() => {
    if (errorTenants) {
      return <div>Error loading data</div>;
    }

    if (isPendingTenants) {
      return <ProgressBar animate="spinning" />;
    }

    const tenantsItems = tenants.items;

    if (tenantsItems.length) {
      return (
        <Box>
          <Grid>
            {tenantsItems.map(({ id, name, organization, usersCount }) => (
              <Cell.Basic colSpan={[4, 3, 4]} key={id}>
                <TenantCard
                  id={id}
                  name={name}
                  organization={organization}
                  usersCount={usersCount}
                />
              </Cell.Basic>
            ))}
          </Grid>
        </Box>
      );
    }

    if (tenantsItems.length === 0 && !searchQuery) {
      return (
        <EmptyState
          description={t('common:emptyState.tenants')}
          headline={t('common:emptyState.textNoX', {
            x: t('common:glossary.tenant_other'),
          })}
          illustrationConfig={{
            img: 'ClearedData',
          }}
        />
      );
    }

    return (
      <EmptyState
        description={t('common:emptyState.tryDifferentTerm')}
        headline={t('common:emptyState.noXfound', {
          x: t('common:glossary.tenant'),
        })}
        illustrationConfig={{
          img: 'SearchNotFound',
        }}
      />
    );
  }, [errorTenants, isPendingTenants, tenants]);

  const onCreateTenant = () => navigate('new');

  return (
    <>
      {canCreateTenant ? (
        <ActionBar onSearch={handleSearch}>
          <Button icon="Plus" onClick={onCreateTenant} size="m">
            {t('common:action.createX', { x: t('common:glossary.tenant') })}
          </Button>
        </ActionBar>
      ) : null}

      {renderResult()}
    </>
  );
};
